import Resizer from 'react-image-file-resizer';
import heic2any from 'heic2any';

const IMAGE_PROCESSING_TIMEOUT_MS = 20000;

const calculateContainedImageSize = (
    sourceWidth,
    sourceHeight,
    maxWidth,
    maxHeight
) => {
    if (
        ![sourceWidth, sourceHeight, maxWidth, maxHeight].every(
            (value) => Number.isFinite(value) && value > 0
        )
    ) {
        throw new Error('Invalid image dimensions.');
    }

    const scale = Math.min(
        1,
        maxWidth / sourceWidth,
        maxHeight / sourceHeight
    );
    return {
        width: Math.max(1, Math.round(sourceWidth * scale)),
        height: Math.max(1, Math.round(sourceHeight * scale)),
    };
};

const resizeWithCanvas = (file, maxWidth, maxHeight, quality) =>
    new Promise((resolve, reject) => {
        let settled = false;
        const reader = new FileReader();
        const timeoutId = setTimeout(() => {
            finish(new Error('Image processing timed out.'));
        }, IMAGE_PROCESSING_TIMEOUT_MS);

        const finish = (error, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            if (error) {
                reject(error);
                return;
            }
            resolve(value);
        };

        reader.onerror = () => finish(new Error('Could not read image file.'));
        reader.onload = () => {
            const image = new Image();
            image.onerror = () =>
                finish(new Error('Could not decode image file.'));
            image.onload = () => {
                try {
                    const sourceWidth = image.naturalWidth || image.width;
                    const sourceHeight = image.naturalHeight || image.height;
                    const target = calculateContainedImageSize(
                        sourceWidth,
                        sourceHeight,
                        maxWidth,
                        maxHeight
                    );
                    const canvas = document.createElement('canvas');
                    canvas.width = target.width;
                    canvas.height = target.height;
                    const context = canvas.getContext('2d');
                    if (!context) {
                        throw new Error('Canvas is unavailable.');
                    }
                    context.imageSmoothingEnabled = true;
                    context.imageSmoothingQuality = 'high';
                    context.drawImage(image, 0, 0, target.width, target.height);
                    finish(
                        null,
                        canvas.toDataURL('image/jpeg', quality / 100)
                    );
                } catch (error) {
                    finish(error);
                }
            };
            image.src = reader.result;
        };

        try {
            reader.readAsDataURL(file);
        } catch (error) {
            finish(error);
        }
    });

const readFilesAndExtractURI = async (file, resizeOptions = {}) => {
    const {
        maxWidth: configuredMaxWidth,
        maxHeight: configuredMaxHeight,
        quality = 100,
    } = resizeOptions;

    if (configuredMaxWidth && configuredMaxHeight) {
        return resizeWithCanvas(
            file,
            configuredMaxWidth,
            configuredMaxHeight,
            quality
        );
    }

    let minimumSize = 512;
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
            let image = new Image();
            image.onload = async function () {
                // if (image.width < 512 || image.height < 512) {
                //   alert("사진의 크기가 너무 작습니다.");
                //   reject("사진의 크기가 너무 작습니다.");
                // }
                if (image.width >= image.height) {
                    // 이미지의 가로길이가 세로길이보다 길거나 같을 때 (정방형일때)
                    // get proportion of the image
                    let proportion = image.width / image.height;
                    // resize image
                    Resizer.imageFileResizer(
                        file,
                        minimumSize * proportion,
                        minimumSize,
                        'JPEG',
                        100,
                        0,
                        (uri) => {
                            resolve(uri);
                        },
                        'base64'
                    );
                } else {
                    // 이미지의 세로길이가 가로길이보다 길때
                    // get proportion of the image
                    let proportion = image.height / image.width;
                    // resize image
                    Resizer.imageFileResizer(
                        file,
                        minimumSize,
                        minimumSize * proportion,
                        'JPEG',
                        100,
                        0,
                        (uri) => {
                            resolve(uri);
                        },
                        'base64'
                    );
                }
            };
            image.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const changeFileToJPG = async (file) => {
    return new Promise((resolve, reject) => {
        try {
            if (file.type === 'image/heic') {
                heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 1,
                })
                    .then((result) => {
                        let resultFile = new File(
                            [result],
                            file.name.split('.')[0] + '.jpg',
                            {
                                type: 'image/jpeg',
                                lastModified: new Date().getTime(),
                            }
                        );

                        resolve(resultFile);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } else {
                resolve(file);
            }
        } catch (error) {
            reject(error);
        }
    });
};

const autoCropImages = (file) => {
    return new Promise((resolve, reject) => {
        // create canvas and draw file, auto crop the image
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');
        let image = new Image();
        image.onload = function () {
            let base_length = 1024;
            let mapped_width = base_length;
            let mapped_height = base_length;
            canvas.width = base_length;
            canvas.height = base_length;
            // center the image
            let offsetX = 0;
            let offsetY = 0;
            // if image size is smaller than canvas size, set base size to image size
            if (image.width < base_length || image.height < base_length) {
                base_length =
                    image.width > image.height ? image.height : image.width;
            }
            if (image.width > image.height) {
                offsetX = Math.abs(image.width - base_length) / 2;
            } else {
                offsetY = Math.abs(image.height - base_length) / 2;
            }
            ctx.drawImage(
                image,
                offsetX,
                offsetY,
                base_length,
                base_length,
                0,
                0,
                1024,
                1024
            );
            resolve({
                offsetX,
                offsetY,
                canvas: canvas.toDataURL('image/jpeg'),
            });
        };
        image.src = file;
    });
};

export {
    calculateContainedImageSize,
    readFilesAndExtractURI,
    changeFileToJPG,
    autoCropImages,
};
